import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { ArrowLeft, CheckCircle, XCircle, Save, Plus, Clock, Phone, Building2, Zap, Trash2 } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  "New Lead": "bg-blue-50 text-blue-700 border-blue-100",
  "Qualified Clinic": "bg-emerald-50 text-emerald-700 border-emerald-100",
  "Missing Provider": "bg-amber-50 text-amber-700 border-amber-100",
  "Not a Fit": "bg-red-50 text-red-600 border-red-100",
  "Live Team Handoff": "bg-purple-50 text-purple-700 border-purple-100",
  "Qualified Vendor Partner": "bg-indigo-50 text-indigo-700 border-indigo-100",
  "Needs Review": "bg-slate-100 text-slate-600 border-slate-200",
};

const ALL_STATUSES = ["New Lead", "Incomplete Intake", "Needs Review", "Qualified Clinic", "Qualified Vendor Partner",
  "Missing Provider", "Not a Fit", "Live Team Handoff", "Pharmacy Pathway", "Lab Pathway",
  "Telehealth Pathway", "Compliance Review", "Closed / Connected"];

function InfoRow({ label, value }) {
  if (!value && value !== false) return null;
  return (
    <div className="flex gap-4 py-2 border-b border-slate-50 last:border-0">
      <dt className="text-xs font-medium text-slate-400 w-40 flex-shrink-0 pt-0.5">{label}</dt>
      <dd className="text-sm text-slate-700 flex-1">
        {Array.isArray(value) ? (
          value.length > 0 ? value.join(", ") : "—"
        ) : typeof value === "boolean" ? (
          value ? <span className="text-emerald-600">Yes</span> : <span className="text-red-500">No</span>
        ) : value || "—"}
      </dd>
    </div>
  );
}

function parseTranscript(raw) {
  if (!raw) return [];
  return raw.split("\n").filter(line => line.trim()).map(line => {
    const match = line.match(/^([^:]+):\s*(.+)$/);
    if (match) return { speaker: match[1].trim(), text: match[2].trim() };
    return { speaker: null, text: line.trim() };
  });
}

export default function AdminLeadDetail() {
  const { id } = useParams();
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [pathway, setPathway] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/leads/${id}`, { headers: authHeader() })
      .then(res => {
        setData(res.data);
        setNewStatus(res.data.lead?.lead_status || "New Lead");
        setAssignedTo(res.data.lead?.assigned_to || "");
        setPathway(res.data.lead?.recommended_pathway || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id, authHeader]);

  const saveChanges = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/leads/${id}`, {
        lead_status: newStatus,
        assigned_to: assignedTo || null,
        recommended_pathway: pathway || null,
      }, { headers: authHeader() });
      setData(prev => ({ ...prev, lead: { ...prev.lead, lead_status: newStatus } }));
    } catch {}
    setSaving(false);
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/leads/${id}/notes`, { note }, { headers: authHeader() });
      const res = await axios.get(`${API}/leads/${id}`, { headers: authHeader() });
      setData(res.data);
      setNote("");
    } catch {}
    setSavingNote(false);
  };

  const deleteLead = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeader() });
      navigate("/admin/dashboard");
    } catch {}
    setDeleting(false);
  };

  if (loading) return (
    <AdminLayout title="Lead Detail">
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
      </div>
    </AdminLayout>
  );

  if (!data) return (
    <AdminLayout title="Lead Detail">
      <div className="text-center py-20 text-slate-500">Lead not found.</div>
    </AdminLayout>
  );

  const { lead, notes, activity, suggested_partners, team } = data;

  return (
    <AdminLayout title="Lead Detail">
      <div data-testid={ADMIN.leadDetailPage} className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/dashboard" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-900">
              {lead.first_name} {lead.last_name}
              {lead.company_name && <span className="text-slate-400 font-normal text-base"> · {lead.company_name}</span>}
            </h2>
            <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium border mt-1 ${STATUS_COLORS[lead.lead_status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {lead.lead_status || "New Lead"}
            </span>
          </div>
          <button
            data-testid="delete-lead-btn"
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm border border-red-200 text-red-500 rounded-full hover:bg-red-50 hover:border-red-300 transition-all"
          >
            <Trash2 size={14} /> Delete Lead
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Lead Info */}
          <div className="lg:col-span-2 space-y-4">
            {/* Contact */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Contact Information</h3>
              <dl className="space-y-0">
                <InfoRow label="Name" value={`${lead.first_name || ""} ${lead.last_name || ""}`.trim()} />
                <InfoRow label="Email" value={lead.email} />
                <InfoRow label="Phone" value={lead.phone} />
                <InfoRow label="Role" value={lead.role} />
              </dl>
            </div>

            {/* Clinic */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Clinic / Organization</h3>
              <dl className="space-y-0">
                <InfoRow label="Company" value={lead.company_name} />
                <InfoRow label="Website" value={lead.website} />
                <InfoRow label="Location" value={[lead.city, lead.state].filter(Boolean).join(", ")} />
                <InfoRow label="Locations" value={lead.number_of_locations} />
                <InfoRow label="States Served" value={lead.states_served} />
                <InfoRow label="Clinic Types" value={lead.clinic_types} />
              </dl>
            </div>

            {/* Provider */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Provider Status</h3>
              <dl className="space-y-0">
                <InfoRow label="Licensed Provider" value={lead.has_licensed_provider} />
                <InfoRow label="NPI" value={lead.has_npi} />
                <InfoRow label="Medical Director" value={lead.has_medical_director} />
                <InfoRow label="License Types" value={lead.license_types} />
              </dl>
            </div>

            {/* Services */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Services & Volume</h3>
              <dl className="space-y-0">
                <InfoRow label="Requested Pathways" value={lead.requested_pathways} />
                <InfoRow label="Current Situation" value={lead.current_situation} />
                <InfoRow label="Monthly Patients" value={lead.monthly_patient_volume} />
                <InfoRow label="Target Patients" value={lead.target_patient_volume} />
                <InfoRow label="Fulfillment Volume" value={lead.expected_fulfillment_volume} />
                <InfoRow label="Timeline" value={lead.timeline_to_launch} />
              </dl>
            </div>

            {/* Compliance */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Compliance Acknowledgments</h3>
              <div className="space-y-2">
                {[
                  { key: "compliance_ack_medical_advice", label: "No medical advice" },
                  { key: "compliance_ack_no_ruo", label: "No research-use-only products" },
                  { key: "compliance_ack_provider_decisions", label: "Provider-led decisions" },
                  { key: "compliance_ack_subject_to_law", label: "Subject to applicable law" },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {lead[key] ? <CheckCircle size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-slate-300" />}
                    <span className={lead[key] ? "text-slate-700" : "text-slate-400"}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Call Transcript — only for phone leads */}
            {lead.transcript && (
              <div data-testid="lead-transcript-section" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-cyan-50 flex items-center justify-center">
                    <Phone size={14} className="text-cyan-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">Call Transcript</h3>
                  {lead.call_id && (
                    <span className="ml-auto text-xs text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded-md">
                      {lead.call_id.slice(0, 16)}…
                    </span>
                  )}
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {parseTranscript(lead.transcript).map((line, idx) => {
                    const isAgent = line.speaker && (line.speaker.toLowerCase().includes("agent") || line.speaker.toLowerCase().includes("ava"));
                    return (
                      <div key={idx} className={`flex gap-3 ${isAgent ? "" : "flex-row-reverse"}`}>
                        <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold mt-0.5 ${isAgent ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"}`}>
                          {line.speaker ? line.speaker[0].toUpperCase() : "?"}
                        </div>
                        <div className={`flex flex-col max-w-[80%] ${isAgent ? "items-start" : "items-end"}`}>
                          {line.speaker && (
                            <span className="text-xs text-slate-400 mb-1">{line.speaker}</span>
                          )}
                          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${isAgent ? "bg-blue-50 text-slate-800 rounded-tl-sm" : "bg-slate-100 text-slate-700 rounded-tr-sm"}`}>
                            {line.text}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right: Actions */}
          <div className="space-y-4">
            {/* Status Update */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Update Lead</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                  <select data-testid={ADMIN.leadStatusSelect}
                    value={newStatus} onChange={e => setNewStatus(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white">
                    {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Assign To</label>
                  <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white">
                    <option value="">Unassigned</option>
                    {(team || []).map(m => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Recommended Pathway</label>
                  <input value={pathway} onChange={e => setPathway(e.target.value)}
                    placeholder="e.g., 503A Pharmacy + Lab"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
                </div>
                <button data-testid={ADMIN.updateStatusBtn}
                  onClick={saveChanges} disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-all">
                  <Save size={15} />
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>

            {/* Suggested Partners */}
            {suggested_partners?.length > 0 && (
              <div data-testid="suggested-partners-section" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Building2 size={13} className="text-indigo-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">Suggested Partners</h3>
                  <span className="ml-auto text-xs text-slate-400">{suggested_partners.length} match{suggested_partners.length !== 1 ? "es" : ""}</span>
                </div>
                <div className="space-y-2">
                  {suggested_partners.map(p => (
                    <div key={p.id} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{p.partner_name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{p.partner_type}</p>
                        </div>
                        {p.match_score > 0 && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Zap size={11} className="text-indigo-500" />
                            <span className="text-xs font-semibold text-indigo-600">{p.match_score}</span>
                          </div>
                        )}
                      </div>
                      {p.matching_pathways?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {p.matching_pathways.map((mp, i) => (
                            <span key={i} className="inline-block px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-600 border border-indigo-100 truncate max-w-full">
                              {mp}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Internal Notes</h3>
              <div className="space-y-3 mb-3 max-h-48 overflow-y-auto">
                {(notes || []).length === 0 ? (
                  <p className="text-xs text-slate-400">No notes yet.</p>
                ) : notes.map(n => (
                  <div key={n.id} className="p-3 bg-slate-50 rounded-xl">
                    <p className="text-xs text-slate-500 mb-1">{n.user_name} · {new Date(n.created_at).toLocaleDateString()}</p>
                    <p className="text-sm text-slate-700">{n.note}</p>
                  </div>
                ))}
              </div>
              <textarea data-testid={ADMIN.noteInput}
                value={note} onChange={e => setNote(e.target.value)}
                placeholder="Add a note..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 mb-2"
              />
              <button data-testid={ADMIN.addNoteBtn}
                onClick={addNote} disabled={savingNote || !note.trim()}
                className="w-full flex items-center justify-center gap-2 border border-slate-200 text-slate-700 py-2 rounded-full text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-all">
                <Plus size={14} />
                {savingNote ? "Adding..." : "Add Note"}
              </button>
            </div>

            {/* Activity */}
            {(activity || []).length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Activity</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {activity.map(a => (
                    <div key={a.id} className="flex gap-2 text-xs">
                      <Clock size={12} className="text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-slate-700">{a.activity_description}</p>
                        <p className="text-slate-400">{new Date(a.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-red-50 mx-auto mb-4">
              <Trash2 size={22} className="text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-slate-900 text-center mb-1">Delete this lead?</h3>
            <p className="text-sm text-slate-500 text-center mb-6">
              <span className="font-medium text-slate-700">{lead.first_name} {lead.last_name}</span>
              {lead.company_name && <> · {lead.company_name}</>} will be permanently removed. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 text-sm border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={deleteLead} disabled={deleting}
                data-testid="confirm-delete-lead-btn"
                className="flex-1 flex items-center justify-center gap-2 bg-red-500 text-white py-2.5 rounded-full text-sm font-medium hover:bg-red-600 disabled:opacity-50 transition-all">
                {deleting ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Trash2 size={14} />}
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
